import React, { useState } from "react";
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Grid,
  TextField,
  MenuItem,
  Stack,
  Typography,
  Skeleton,
  Box,
  ThemeProvider,
  createTheme,
} from "@material-ui/core";
import LocalizationProvider from "@material-ui/lab/LocalizationProvider";
import DatePicker from "@material-ui/lab/DatePicker";
import AdapterDateFns from "@material-ui/lab/AdapterDateFns";
import es from "date-fns/locale/es";
import { facturasApi } from "src/services/facturasApi";
import { sucursalesApi } from "src/services/sucursalesApi";
import Label from "src/components/Label";
import { formatearFecha } from "src/utils/formatTime";
import { sentenceCase } from "change-case";
import CustomTablePagination from "src/components/CustomTablePagination";
import * as locales from "@material-ui/core/locale";
import RegistrarPagoModal from "./registrarPagos";

export default function ValidacionPagos({ tabIndex }) {
  // Estados para filtros
  const [filtros, setFiltros] = useState({
    sucursal: "TODAS",
    fechaInicio: null,
    fechaFin: null,
    empleado: "",
  });

  const [modalPagoAbierto, setModalPagoAbierto] = useState(false);
  const [facturaSeleccionada, setFacturaSeleccionada] = useState(null);

  // Estados para paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Queries
  const { data: sucursales = [] } = sucursalesApi.useGetSucursalesQuery();

  const isValidDate = (date) => date instanceof Date && !isNaN(date.getTime());

  const { data: facturasPendientes = [], isLoading: cargandoFacturas } =
    facturasApi.useGetFacturasPendientesPagoEmpleadosQuery(
      {
        ...filtros,
        fechaInicio: isValidDate(filtros.fechaInicio)
          ? formatearFecha(filtros?.fechaInicio, "YYYY/MM/DD")
          : "",
        fechaFin: isValidDate(filtros.fechaFin)
          ? formatearFecha(filtros?.fechaFin, "YYYY/MM/DD")
          : "",
      },
      {
        skip: tabIndex !== 2,
      },
    );

  const abrirModalPago = (factura) => {
    setFacturaSeleccionada(factura);
    setModalPagoAbierto(true);
  };

  const cerrarModalPago = () => {
    setModalPagoAbierto(false);
    setFacturaSeleccionada(null);
  };

  const handleFiltroChange = (campo, valor) => {
    setFiltros((prev) => ({ ...prev, [campo]: valor }));
    setPage(0); // Reset página al cambiar filtros
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Limpiar filtros
  const limpiarFiltros = () => {
    setFiltros({
      sucursal: "TODAS",
      fechaInicio: null,
      fechaFin: null,
      empleado: "",
    });
    setPage(0);
  };

  // Paginación local
  const facturasPaginadas = facturasPendientes.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  if (tabIndex !== 2) return null;

  return (
    <>
      <Card>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">
              Validación de Pagos a Empleados
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {facturasPendientes.length} facturas pendientes
            </Typography>
          </Box>

          {/* Filtros */}
          <Grid container spacing={2} style={{ marginBottom: 24 }}>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                label="Sucursal"
                value={filtros.sucursal}
                onChange={(e) => handleFiltroChange("sucursal", e.target.value)}
                size="small"
              >
                <MenuItem value="TODAS">Todas las sucursales</MenuItem>
                {sucursales.map((sucursal) => (
                  <MenuItem key={sucursal.nombre} value={sucursal.nombre}>
                    {sucursal.nombre}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={2}>
              <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
                <DatePicker
                  label="Fecha Inicio"
                  value={filtros.fechaInicio}
                  onChange={(newValue) =>
                    handleFiltroChange("fechaInicio", newValue)
                  }
                  inputFormat="dd/MM/yyyy"
                  disableMaskedInput
                  renderInput={(params) => (
                    <TextField {...params} fullWidth size="small" />
                  )}
                  maxDate={filtros.fechaFin || undefined}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} md={2}>
              <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
                <DatePicker
                  label="Fecha Fin"
                  value={filtros.fechaFin}
                  onChange={(newValue) =>
                    handleFiltroChange("fechaFin", newValue)
                  }
                  inputFormat="dd/MM/yyyy"
                  disableMaskedInput
                  renderInput={(params) => (
                    <TextField {...params} fullWidth size="small" />
                  )}
                  minDate={filtros.fechaInicio || undefined}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Buscar por RUT, Tarea o folio"
                value={filtros.empleado}
                onChange={(e) => handleFiltroChange("empleado", e.target.value)}
                placeholder="Ej: 123456789, Planificación mensual o 118563"
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Button
                variant="outlined"
                onClick={limpiarFiltros}
                size="small"
                fullWidth
              >
                Limpiar Filtros
              </Button>
            </Grid>
          </Grid>

          {/* Tabla de facturas */}
          <TableContainer>
            {!cargandoFacturas ? (
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                    >
                      Folio
                    </TableCell>
                    <TableCell
                      style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                    >
                      Cliente
                    </TableCell>
                    <TableCell
                      style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                    >
                      Empleado
                    </TableCell>
                    <TableCell
                      style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                    >
                      Tarea
                    </TableCell>
                    <TableCell
                      style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                    >
                      Total
                    </TableCell>
                    <TableCell
                      style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                    >
                      Estado factura
                    </TableCell>
                    <TableCell
                      style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                    >
                      Fecha validación
                    </TableCell>
                    <TableCell
                      style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                    >
                      Sucursal
                    </TableCell>
                    <TableCell
                      style={{
                        fontWeight: "bold",
                        backgroundColor: "#f5f5f5",
                        minWidth: 120,
                      }}
                    >
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {facturasPaginadas.map((factura) => (
                    <TableRow key={factura.id} hover>
                      <TableCell>
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          {factura.folio}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {factura.nombreCliente}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {`${factura.nombreEmpleado || ""} ${
                            factura.apellidosEmpleado || ""
                          }`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {factura.nombreTarea}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          ${factura.total?.toLocaleString("es-CL")}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="column" spacing={0.5}>
                          <Label variant="ghost" color="info">
                            {sentenceCase(factura.estadoFactura)}
                          </Label>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatearFecha(
                            factura.fechaValidacionSupervisor,
                            "DD/MM/YYYY",
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {factura.sucursal}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          style={{ minWidth: 120 }}
                          onClick={() => abrirModalPago(factura)}
                        >
                          Registrar Pago
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}

                  {facturasPendientes.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={9}
                        align="center"
                        style={{ padding: 48 }}
                      >
                        <Typography
                          variant="h6"
                          color="textSecondary"
                          gutterBottom
                        >
                          📋 No hay facturas pendientes
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          No se encontraron facturas pendientes de pago a
                          empleados
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            ) : (
              <Box p={2}>
                <Skeleton variant="rectangular" width="100%" height="400px" />
              </Box>
            )}
          </TableContainer>

          <Box display="flex" justifyContent="flex-end">
            <ThemeProvider
              theme={(outerTheme) => createTheme(outerTheme, locales["esES"])}
            >
              <CustomTablePagination
                count={facturasPendientes.length}
                rowsPerPage={rowsPerPage}
                page={page}
                colSpan={9}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 15, 50]}
              />
            </ThemeProvider>
          </Box>
        </CardContent>
      </Card>
      {modalPagoAbierto && (
        <RegistrarPagoModal
          open={modalPagoAbierto}
          onClose={cerrarModalPago}
          factura={facturaSeleccionada}
        />
      )}
    </>
  );
}
